import { CommonModule, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    NgbActiveModal,
    NgbDropdownModule,
    NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { PushPipe } from '@ngrx/component';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import {
    EntityAgGridTableComponent,
    SERVER_SIDE_DEFAULT_GRID_OPTIONS,
    WdxColDef,
} from '@wdx/shared/components/ag-grid';
import {
    WdxButtonModule,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIsLoadingModule } from '@wdx/shared/components/wdx-is-loading';
import { WdxSearchInputComponent } from '@wdx/shared/components/wdx-search-input';
import {
    FormFrameworkEvent,
    FormFrameworkEventType,
    FormFrameworkSaveSuccessEvent,
    FormInitialisationParametersService,
    FormInputData,
    WdxFormFrameworkModule,
} from '@wdx/shared/infrastructure/form-framework';
import {
    ACTIVITY_QUICK_CREATE_FORMS,
    ActivityQuickCreateForm,
    ENTITY_FORM_ID_MAP,
    LookupCreationMode,
    LookupFieldResult,
    LookupObject,
    LookupSourceType,
    Query,
    QueryExpression,
    SystemEntity,
    TranslateTokenisedStringPipe,
    UTILS_ICON_ADD,
    View,
} from '@wdx/shared/utils';
import {
    GridApi,
    GridOptions,
    ModuleRegistry,
    RowSelectedEvent,
    RowSelectionModule,
} from 'ag-grid-community';
import {
    AllCommunityModule,
    ServerSideRowModelApiModule,
    ServerSideRowModelModule,
} from 'ag-grid-enterprise';
import { Observable, tap } from 'rxjs';
import { DynamicEntityHelperService } from '../../../../features/dynamic-pages/services/dynamic-entity-helper.service';
import { ViewsFacadeService } from '../../../../state/views/views-facade.service';
import { FormLookupSelectedItemsComponent } from '../lookup-selected-items/lookup-selected-items.component';

ModuleRegistry.registerModules([
    AllCommunityModule,
    RowSelectionModule,
    ServerSideRowModelModule,
    ServerSideRowModelApiModule,
]);

@Component({
    selector: 'clmi-form-lookup-table-modal',
    templateUrl: './form-lookup-table-modal.component.html',
    styleUrls: ['./form-lookup-table-modal.component.scss'],
    imports: [
        WdxIsLoadingModule,
        NgSelectModule,
        FormsModule,
        WdxSearchInputComponent,
        WdxButtonModule,
        WdxIconModule,
        TranslateTokenisedStringPipe,
        PushPipe,
        EntityAgGridTableComponent,
        UpperCasePipe,
        NgTemplateOutlet,
        FormLookupSelectedItemsComponent,
        WdxFormFrameworkModule,
        CommonModule,
        NgbTooltipModule,
        NgbDropdownModule,
    ],
    providers: [DynamicEntityHelperService, PartyToAvatarPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'd-flex flex-column h-100' },
})
export class FormLookupTableModalComponent implements OnInit {
    private modal = inject(NgbActiveModal);
    private viewsFacade = inject(ViewsFacadeService);
    private dynamicEntityHelperService = inject(DynamicEntityHelperService);
    private cdr = inject(ChangeDetectorRef);
    private initParamsService = inject(FormInitialisationParametersService);

    @Input() isMulti: boolean;
    @Input() formInputData: FormInputData;
    @Input() value?: Array<LookupFieldResult | LookupObject>;
    @Input() layoutQueryExpressions: QueryExpression[] = [];
    @Input() formData?: Record<string, any>;

    @Output() selectedTypeChanged = new EventEmitter<LookupSourceType>();

    public gridOptions: GridOptions;
    public columnDefs: WdxColDef[] = [];
    public searchText: string | undefined;
    public selectedType: LookupSourceType;
    public selectedTypeFormId: string;
    public typeFormIds: Record<string, string>;
    public selectedView: View;
    public views$: Observable<View[]>;
    public queryExpressions: QueryExpression[] = [];
    public initialSort?: Pick<Query, 'sortBy' | 'sortDirection'>;
    public initialisationParams: Record<string, any>;

    public readonly WdxButtonStyle = WdxButtonStyle;
    public readonly LookupCreationMode = LookupCreationMode;
    public readonly LookupSourceType = LookupSourceType;
    public readonly ICON_ADD = UTILS_ICON_ADD;
    public activityQuickCreateForms: ActivityQuickCreateForm[] =
        ACTIVITY_QUICK_CREATE_FORMS;

    private gridApi: GridApi;

    public ngOnInit(): void {
        if (this.formInputData?.initialisationParameters) {
            this.initialisationParams =
                this.initParamsService.createFormFieldInitialisationParams(
                    this.formInputData.initialisationParameters,
                    this.formData,
                );
        }
        this.typeFormIds = this.formInputData?.lookupSources?.length
            ? this.formInputData.lookupSources.reduce(
                  (acc, lookupSource) => {
                      const formId =
                          lookupSource.quickCreateFormName ||
                          ENTITY_FORM_ID_MAP[lookupSource.type];
                      return {
                          ...acc,
                          [lookupSource.type]: formId,
                      };
                  },
                  {} as Record<string, string>,
              )
            : {};

        this.typeChanged(this.formInputData?.lookupSources?.[0]?.type);
        this.gridOptions = {
            ...SERVER_SIDE_DEFAULT_GRID_OPTIONS,
            rowSelection: { mode: this.isMulti ? 'multiRow' : 'singleRow' },
        };
    }

    public searchChange(searchText?: string) {
        this.searchText = searchText;
    }

    public viewChanged() {
        this.gridApi?.deselectAll();
        this.initialSort = this.selectedView?.filter;
        this.queryExpressions = [
            ...(this.layoutQueryExpressions ?? []),
            ...(this.selectedView?.filter?.expressions ?? []),
        ];
    }

    public typeChanged(type: LookupSourceType) {
        if (this.selectedType !== type) {
            this.selectedTypeChanged.emit(type);
            this.selectedType = type;
            this.selectedView = undefined;
        }
        this.views$ = this.viewsFacade
            .getViewsForEntityType$(SystemEntity[type], undefined, {
                isLookup: true,
            })
            .pipe(
                tap((views) => {
                    if (this.selectedView === undefined) {
                        const viewCode =
                            this.formInputData?.lookupConfiguration?.viewCode;
                        this.selectedView =
                            (viewCode &&
                                views.find((view) => view.id === viewCode)) ||
                            views[0];
                        this.gridApi?.deselectAll();
                        this.columnDefs = this.selectedView
                            ? this.selectedView.configuration.columns.map(
                                  (col) =>
                                      this.dynamicEntityHelperService.createColDef(
                                          SystemEntity[type],
                                          col,
                                          { disable: true },
                                          [],
                                      ),
                              )
                            : [];
                    }
                    this.initialSort = this.selectedView?.filter;
                    this.queryExpressions = [
                        ...(this.layoutQueryExpressions ?? []),
                        ...(this.selectedView?.filter?.expressions ?? []),
                    ];
                }),
            );
        this.closeCreateNewForm();
    }

    public cancelClicked() {
        this.modal.dismiss();
    }

    public okClicked() {
        this.modal.close({
            rows: this.value,
        });
    }

    public setGridApi(gridApi: GridApi) {
        this.gridApi = gridApi;
        this.gridApi.addEventListener(
            'rowSelected',
            (event: RowSelectedEvent) => {
                const data = event.node.data;
                const id = data?.id ?? data?.code;

                if (event.source.startsWith('api') || !id) {
                    return;
                }

                const existing = this.value?.find((x) => x.id === id);

                if (event.node.isSelected()) {
                    if (existing) {
                        return;
                    }
                    const { name, subject } = data;
                    this.value = [
                        ...(this.isMulti ? (this.value ?? []) : []),
                        {
                            id,
                            name: name ?? subject ?? id,
                            lookupType: this.selectedType,
                        },
                    ];
                } else {
                    if (existing) {
                        this.value = this.value.filter(
                            (lookup) => lookup.id !== existing.id,
                        );
                    }
                }
                this.cdr.markForCheck();
            },
        );
    }

    public tableDataLoaded() {
        this.gridApi.getSelectedRows();
        const originalSelections = this.value
            ?.filter((lookup) => lookup.lookupType === this.selectedType)
            .map(({ id }) => id)
            .filter(Boolean);
        if (originalSelections?.length) {
            this.gridApi.forEachNode((node) => {
                const id = node.data?.id ?? node.data?.code;

                if (id && originalSelections.includes(id)) {
                    node.setSelected(true);
                }
            });
        }
    }

    public deleteItem(value: LookupFieldResult | LookupObject) {
        this.removeItem(value);
        this.gridApi.getSelectedNodes().forEach((node) => {
            const id = node.data?.id ?? node.data?.code;

            if (id === value.id) {
                node.setSelected(false);
            }
        });
    }

    private removeItem(value: LookupFieldResult | LookupObject) {
        this.value = this.value?.filter(({ id }) => id !== value.id);
        this.cdr.markForCheck();
    }

    public toggleCreateNewForm(): void {
        const formId = this.typeFormIds[this.selectedType];
        if (this.selectedTypeFormId === formId) {
            this.closeCreateNewForm();
        } else {
            this.selectedTypeFormId = formId;
        }
    }

    public openActivityForm(activityForm: ActivityQuickCreateForm): void {
        if (this.selectedTypeFormId === activityForm.formId) {
            this.closeCreateNewForm();
        } else {
            this.selectedTypeFormId = activityForm.formId;
        }
    }

    public get isActivityType(): boolean {
        return this.selectedType === LookupSourceType.Activity;
    }

    public closeCreateNewForm(): void {
        this.selectedTypeFormId = undefined;
    }

    public onFormEvent(event: FormFrameworkEvent) {
        if (event.type === FormFrameworkEventType.SaveSuccess) {
            this.gridApi.refreshServerSide({ purge: true });
            if (event.action?.entityId) {
                const name =
                    event.action.savedFormData?.name ??
                    event.action.savedFormData?.subject ??
                    (event.action.savedFormData?.firstName
                        ? `${event.action.savedFormData?.firstName} ${event.action.savedFormData?.lastName}`
                        : undefined) ??
                    event.action.savedFormData?.code;
                this.value = [
                    ...(this.value ?? []),
                    {
                        id: event.action.entityId,
                        name,
                        lookupType: this.selectedType,
                    },
                ];
                this.cdr.markForCheck();
            }
            this.closeCreateNewForm();
        }
    }
}
